import { assign, fromPromise, setup } from 'xstate';
import { showAxiosErrorAction } from '../../actions';
import {
  closeModal,
  filterMedicalLicenseTable,
  openCreateModal,
  openDeleteConfirmation,
  openEditModal,
  removeLicense,
  resetMachine,
  setIdToDelete,
  setMedicalLicenses,
  setMedicalLicenseTypeOptions,
  setMedicalSort,
  toggleLoading,
  updateLicenses,
} from './actions';
import { API_CALLS, getMedicalTypes } from './api';
import { Events } from './events';
import { MedicalLicenseContext } from './types';

import { emptyForm } from './constants';

export const medLicenseMachine = setup({
  actors: {
    getMedicalLicenses: fromPromise(API_CALLS.get),
    delete: fromPromise(API_CALLS.delete),
    update: fromPromise(API_CALLS.patch),
    create: fromPromise(API_CALLS.create),
    getMedLicenseTypes: fromPromise(getMedicalTypes),
  },
  types: {
    context: {} as MedicalLicenseContext,
    events: {} as Events,
    input: {} as Partial<MedicalLicenseContext>,
  },
  actions: {
    toggleLoading: assign(toggleLoading),
    sendDeleteConfirmation: openDeleteConfirmation,
    openCreateModal: assign(openCreateModal),
    openEditModal: assign(openEditModal),
    closeModal: assign(closeModal),
    setMedLicenseSort: assign(setMedicalSort),
    setMedTypeOptions: assign(setMedicalLicenseTypeOptions),
    updateMedLicenses: assign(updateLicenses),
    setMedicalLicenses: assign(setMedicalLicenses),
    filterTable: assign(filterMedicalLicenseTable),
    setIdToDelete: assign(setIdToDelete),
    removeLicense: assign(removeLicense),
    showError: showAxiosErrorAction,
    reset: assign(resetMachine),
  },
}).createMachine({
  context: ({ input }) => ({
    loading: false,
    pagination: {
      page: 1,
      pageSize: 5,
      total: 0,
    },
    modal: {
      edit: false,
      create: false,
    },
    medLicensesForm: emptyForm,
    medicalLicenses: input.medicalLicenses || [],
    idToDelete: -1,
    sort: {
      sortBy: 'id',
      license_type: -1,
      license_number: -1,
      location: -1,
      curr_status: -1,
      expiry_date: -1,
      id: 1,
      page: 1,
      pageSize: 10,
    },
    _medicalLicenseTypeOptions: [],
    filter: {
      status: 'all',
      location: 'all',
      expiration: 'all',
    },
  }),
  initial: '$_VIEW',
  on: {
    ON_OPEN_MODAL: {
      actions: { type: 'openCreateModal' },
    },
    ON_CLOSE_MODAL: {
      actions: { type: 'closeModal' },
      target: '.$_VIEW',
    },
  },
  states: {
    $_VIEW: {
      on: {
        ON_SORT: {
          actions: 'setMedLicenseSort',
          target: '$_LOAD',
        },
        ON_LOAD: {
          actions: {
            type: 'toggleLoading',
          },
          target: `$_LOAD`,
        },
        ON_FILTER: {
          actions: ['filterTable'],
          target: '$_LOAD',
        },
        ON_ADD: {
          actions: ['openCreateModal'],
          target: '$_LOAD_MED_LICENSE_TYPES',
        },
        ON_EDIT: {
          actions: ['openEditModal'],
          // Populate the licence-type choices for edit on a fresh page, just as
          // the add flow already does. The loading state accepts an early Save.
          target: '$_LOAD_MED_LICENSE_TYPES',
        },
        ON_DELETE: {
          actions: ['setIdToDelete'],
          target: '$_DELETE',
        },
        ON_SEND_DELETE_CONFIRMATION: {
          actions: 'sendDeleteConfirmation',
        },
      },
    },
    $_LOAD: {
      invoke: {
        id: 'get',
        src: 'getMedicalLicenses',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: '$_VIEW',
          actions: ['setMedicalLicenses'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['showError', 'reset'],
        },
      },
    },
    $_LOAD_MED_LICENSE_TYPES: {
      on: {
        // The edit modal is already visible while its type choices load. Do not
        // drop a Save if the request is slow.
        ON_UPDATE: {
          target: '$_UPDATE',
        },
      },
      invoke: {
        id: 'get',
        src: 'getMedLicenseTypes',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: '$_FORM',
          actions: ['setMedTypeOptions'],
        },
        onError: {
          target: '$_FORM',
          // Keep the selected record open if its option request fails.
          actions: ['showError'],
        },
      },
    },
    $_FORM: {
      on: {
        ON_SUBMIT: {
          target: '$_SUBMIT',
        },
        ON_UPDATE: {
          target: '$_UPDATE',
        },
      },
    },
    $_SUBMIT: {
      invoke: {
        id: 'createLicense',
        src: 'create',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: '$_VIEW',
          actions: ['updateMedLicenses'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['showError', 'reset'],
        },
      },
    },
    $_UPDATE: {
      invoke: {
        id: 'updateLicense',
        src: 'update',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: '$_VIEW',
          actions: ['updateMedLicenses'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['showError', 'reset'],
        },
      },
    },
    $_DELETE: {
      invoke: {
        id: 'deleteLicense',
        src: 'delete',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: '$_VIEW',
          actions: ['removeLicense'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['showError', 'reset'],
        },
      },
    },
  },
});
